import { Box, CircularProgress, Grid, Stack } from "@mui/material";
import ReactECharts from "echarts-for-react";
import ProgressShape from "../../../assets/learning/progress-shape.svg";
import RosterShape from "../../../assets/learning/roster-shape.svg";
import StepsShape from "../../../assets/learning/steps-shape.svg";
import EventStatisticsContainer from "../../../container/EventStatistics.container";
import VSNSelect from "../../../shared/VSNSelect";
import VSNTypography from "../../../shared/VSNTypography";
import { DownArrowAlt } from "../../../shared/icon";
import { EventScheduleStyle } from "./EventScheduleStyle";

const EventStatistics = () => {
  const {
    eventStatisticsData,
    requiredAssessmentsData,
    annualPhysicalsData,
    eventParticipationData,
    handleChartFilterChange,
    mainFrequency,
    handleMainFrequencyChange,
    localFilters,
    loaders,
    handleDownloadReport,
  } = EventStatisticsContainer();
  const classes = EventScheduleStyle();

  const requiredAssessmentsOptions = Object.entries(
    requiredAssessmentsData?.filters || {}
  )?.map(([label, value]) => ({
    key: label,
    value,
  }));

  const annualPhysicalsOptions = Object.entries(
    annualPhysicalsData?.filters || {}
  )?.map(([label, value]) => ({
    key: label,
    value,
  }));

  const EventParticipationOptions = Object.entries(
    eventParticipationData?.filters || {}
  )?.map(([label, value]) => ({
    key: label,
    value,
  }));

  const eventParticipationAssessmentOptions = Object.entries(
    eventParticipationData?.assessment_filter || {}
  )?.map(([label, value]) => ({
    key: label,
    value,
  }));

  return (
    <Stack gap={4}>
      <Grid container columnSpacing={{ md: 3, xs: 2 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <Stack className={classes.statisticBox}>
            <VSNTypography variant="h5">Total Members in Roster</VSNTypography>
            <VSNTypography variant="h4">
              {eventStatisticsData?.progress_summary?.total_member_in_roster ??
                "0"}
            </VSNTypography>
            <VSNTypography>in Event Schedule</VSNTypography>
            <Box className={classes.rosterShape}>
              <img src={RosterShape} alt="box-shape" />
            </Box>
          </Stack>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Stack className={classes.statisticBox}>
            <VSNTypography variant="h5">Completed All Steps</VSNTypography>
            <VSNTypography variant="h4">
              {eventStatisticsData?.progress_summary
                ?.completed_all_steps_percent ?? "0"}
              %
            </VSNTypography>
            <VSNTypography>
              {eventStatisticsData?.progress_summary?.completed_all_steps_count}{" "}
              members
            </VSNTypography>
            <Box className={classes.stepsShape}>
              <img src={StepsShape} alt="box-shape" />
            </Box>
          </Stack>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Stack className={classes.statisticBox}>
            <VSNTypography variant="h5">In Progress</VSNTypography>
            <VSNTypography variant="h4">
              {eventStatisticsData?.progress_summary?.in_progress_percent ??
                "0"}
              %
            </VSNTypography>
            <VSNTypography>
              {" "}
              {eventStatisticsData?.progress_summary?.in_progress_count} members
            </VSNTypography>
            <Box className={classes.progressShape}>
              <img src={ProgressShape} alt="box-shape" />
            </Box>
          </Stack>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Stack className={classes.statisticBox}>
            <VSNTypography variant="h5">Yet to Begin</VSNTypography>
            <VSNTypography variant="h4">
              {eventStatisticsData?.progress_summary?.yet_to_begin_percent ??
                "0"}
              %
            </VSNTypography>
            <VSNTypography>
              {eventStatisticsData?.progress_summary?.yet_to_begin_count}{" "}
              members
            </VSNTypography>
            <Box className={classes.beginShape}>
              <img src={ProgressShape} alt="box-shape" />
            </Box>
          </Stack>
        </Grid>
      </Grid>
      <Box className={classes.frequencySelect}>
        <VSNSelect
          value={mainFrequency || 0}
          label="Days"
          placeHolder="Days"
          IconComponent={DownArrowAlt}
          onChange={(e) => handleMainFrequencyChange(e.target.value)}
          options={[
            { key: "Days", value: "" },
            {
              value: "last_7_days",
              key: "Last 7 Days",
            },
            {
              value: "last_30_days",
              key: "Last 30 Days",
            },
            {
              value: "90_days",
              key: "Last 90 Days",
            },
            {
              value: "last_last_year",
              key: "Last Year",
            },
          ]}
        />
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={4} xl={4}>
          <Stack className={classes.assessmentBox}>
            <Stack direction={{ sm: "row" }} pr={3}>
              <Stack className={classes.assessmentTitle}>
                <VSNTypography variant="h6">
                  Required Online Assesments Overview
                </VSNTypography>
                <VSNTypography>
                  Track member progress across all required assessments.
                </VSNTypography>
              </Stack>
              <Stack className={classes.assessmentSelect} ml="auto" pl={2}>
                <VSNSelect
                  value={localFilters.requiredAssessments || 0}
                  label="requiredAssessments"
                  IconComponent={DownArrowAlt}
                  onChange={(e) =>
                    handleChartFilterChange(
                      "requiredAssessments",
                      e.target.value
                    )
                  }
                  options={[
                    { key: "All", value: "" },
                    ...(requiredAssessmentsOptions || []),
                  ]}
                  placeHolder="All"
                />
              </Stack>
            </Stack>

            <VSNTypography
              onClick={() =>
                handleDownloadReport(
                  "required_online_assessments_overview_filter"
                )
              }
              color="primary"
              sx={{
                textAlign: "right",
                px: 3,
                pt: { sm: 3, xs: 2 },
                textDecoration: "underline",
                cursor: "pointer",
                fontWeight: 700,
              }}
            >
              Download Report
            </VSNTypography>

            {!loaders?.requiredAssessmentsData ? (
              <Stack px={3} flex={1}>
                {requiredAssessmentsData &&
                Object.keys(requiredAssessmentsData)?.length > 0 ? (
                  <AssessmentPieChart
                    title="Required Online Assessments"
                    completedPercent={
                      requiredAssessmentsData?.completed_percent
                    }
                    notCompletedPercent={
                      requiredAssessmentsData?.not_completed_percent
                    }
                  />
                ) : (
                  <Stack
                    alignItems="center"
                    flex={1}
                    justifyContent="center"
                    minHeight={100}
                  >
                    <VSNTypography
                      fontSize={{ md: 32, sm: 26, xs: 20 }}
                      color="#898994"
                      textAlign="center"
                    >
                      There is no data to show
                    </VSNTypography>
                  </Stack>
                )}
              </Stack>
            ) : (
              <Stack alignItems="center" flex={1} justifyContent="center">
                <CircularProgress />
              </Stack>
            )}
          </Stack>
        </Grid>
        <Grid item xs={12} md={6} lg={4} xl={4}>
          <Stack className={classes.assessmentBox}>
            <Stack direction={{ sm: "row" }} pr={3}>
              <Stack className={classes.assessmentTitle}>
                <VSNTypography variant="h6">Lab Draws Overview</VSNTypography>
                <VSNTypography>
                  Monitor lab draw completion status.
                </VSNTypography>
              </Stack>
            </Stack>

            <VSNTypography
              onClick={() => handleDownloadReport("lab_draws_overview")}
              color="primary"
              sx={{
                textAlign: "right",
                px: 3,
                pt: { sm: 3, xs: 2 },
                textDecoration: "underline",
                cursor: "pointer",
                fontWeight: 700,
              }}
            >
              Download Report
            </VSNTypography>

            {!loaders?.eventStatisticsData ? (
              <Stack px={3} flex={1}>
                {eventStatisticsData?.lab_draws_overview &&
                Object.keys(eventStatisticsData?.lab_draws_overview)?.length >
                  0 ? (
                  <AssessmentPieChart
                    completedPercent={
                      eventStatisticsData?.lab_draws_overview?.completed_percent
                    }
                    notCompletedPercent={
                      eventStatisticsData?.lab_draws_overview
                        ?.not_completed_percent
                    }
                  />
                ) : (
                  <Stack
                    alignItems="center"
                    flex={1}
                    justifyContent="center"
                    minHeight={100}
                  >
                    <VSNTypography
                      fontSize={{ md: 32, sm: 26, xs: 20 }}
                      color="#898994"
                      textAlign="center"
                    >
                      There is no data to show
                    </VSNTypography>
                  </Stack>
                )}
              </Stack>
            ) : (
              <Stack alignItems="center" flex={1} justifyContent="center">
                <CircularProgress />
              </Stack>
            )}
          </Stack>
        </Grid>
        <Grid item xs={12} md={6} lg={4} xl={4}>
          <Stack className={classes.assessmentBox}>
            <Stack direction={{ sm: "row" }} pr={3}>
              <Stack className={classes.assessmentTitle}>
                <VSNTypography variant="h6">
                  Physical Assessments Overview
                </VSNTypography>
                <VSNTypography>
                  Track participation across physical assessments.
                </VSNTypography>
              </Stack>
              <Stack className={classes.assessmentSelect} ml="auto" pl={2}>
                <VSNSelect
                  value={localFilters.annualPhysicals || 0}
                  label="annualPhysicals"
                  IconComponent={DownArrowAlt}
                  onChange={(e) =>
                    handleChartFilterChange("annualPhysicals", e.target.value)
                  }
                  options={[
                    { key: "All", value: "" },
                    ...(annualPhysicalsOptions || []),
                  ]}
                  placeHolder="All"
                />
              </Stack>
            </Stack>

            <VSNTypography
              onClick={() =>
                handleDownloadReport("annual_physicals_overview_filter")
              }
              color="primary"
              sx={{
                textAlign: "right",
                px: 3,
                pt: { sm: 3, xs: 2 },
                textDecoration: "underline",
                cursor: "pointer",
                fontWeight: 700,
              }}
            >
              Download Report
            </VSNTypography>

            {!loaders?.annualPhysicalsData ? (
              <Stack pr={{ sm: 3 }} flex={1}>
                {annualPhysicalsData &&
                Object.keys(annualPhysicalsData)?.length > 0 ? (
                  <AssessmentPieChart
                    completedPercent={annualPhysicalsData?.completed_percent}
                    notCompletedPercent={
                      annualPhysicalsData?.not_completed_percent
                    }
                  />
                ) : (
                  <Stack
                    alignItems="center"
                    flex={1}
                    justifyContent="center"
                    minHeight={100}
                  >
                    <VSNTypography
                      fontSize={{ md: 32, sm: 26, xs: 20 }}
                      color="#898994"
                      textAlign="center"
                    >
                      There is no data to show
                    </VSNTypography>
                  </Stack>
                )}
              </Stack>
            ) : (
              <Stack alignItems="center" flex={1} justifyContent="center">
                <CircularProgress />
              </Stack>
            )}
          </Stack>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Stack className={classes.assessmentBox}>
            <Stack direction={{ sm: "row" }} pr={3}>
              <Stack className={classes.assessmentTitle}>
                <VSNTypography variant="h6">
                  Event Participation Over Time
                </VSNTypography>
                <VSNTypography>
                  View real-time participation across event days
                </VSNTypography>
              </Stack>
              <Stack
                className={classes.assessmentSelect}
                direction={{ sm: "row" }}
                gap={1}
                ml="auto"
                pl={2}
              >
                <VSNSelect
                  value={localFilters.eventParticipation || 0}
                  label="eventParticipation"
                  IconComponent={DownArrowAlt}
                  onChange={(e) =>
                    handleChartFilterChange(
                      "eventParticipation",
                      e.target.value
                    )
                  }
                  options={[
                    { key: "All", value: "" },
                    ...(EventParticipationOptions || []),
                  ]}
                  placeHolder="All"
                />

                {["assessment_completed", "results_viewed"].includes(
                  localFilters.eventParticipation
                ) && (
                  <VSNSelect
                    value={localFilters.eventParticipationAssessment || 0}
                    label="eventParticipationAssessment"
                    IconComponent={DownArrowAlt}
                    onChange={(e) =>
                      handleChartFilterChange(
                        "eventParticipationAssessment",
                        e.target.value
                      )
                    }
                    options={[
                      { key: "All", value: "" },
                      ...(eventParticipationAssessmentOptions || []),
                    ]}
                    placeHolder="All"
                  />
                )}
              </Stack>
            </Stack>
            {!loaders?.eventParticipationData ? (
              <Stack px={3} flex={1}>
                {eventParticipationData?.participation_data &&
                Object.keys(eventParticipationData.participation_data).length >
                  0 ? (
                  <DynamicLineChart
                    data={eventParticipationData?.participation_data}
                  />
                ) : (
                  <Stack
                    alignItems="center"
                    flex={1}
                    justifyContent="center"
                    minHeight={100}
                  >
                    <VSNTypography
                      fontSize={{ md: 32, sm: 26, xs: 20 }}
                      color="#898994"
                      textAlign="center"
                    >
                      There is no data to show
                    </VSNTypography>
                  </Stack>
                )}
              </Stack>
            ) : (
              <Stack alignItems="center" flex={1} justifyContent="center">
                <CircularProgress />
              </Stack>
            )}
          </Stack>
        </Grid>

        <Grid item xs={12} md={6}>
          <Stack className={classes.assessmentBox}>
            <Stack direction="row" pr={3}>
              <Stack className={classes.assessmentTitle}>
                <VSNTypography variant="h6">
                  Critical Response Summary
                </VSNTypography>
                <VSNTypography>
                  Monitor high-risk or flagged responses from members
                </VSNTypography>
              </Stack>
            </Stack>

            {!loaders?.eventStatisticsData ? (
              <Stack
                flex={1}
                px={3}
                sx={{
                  overflowX: "auto",
                  "& .echarts-for-react ": { minWidth: 630 },
                }}
              >
                {eventStatisticsData?.critical_response_summary &&
                Object.keys(eventStatisticsData?.critical_response_summary)
                  ?.length > 0 ? (
                  <CriticalResponseBarChart
                    data={eventStatisticsData?.critical_response_summary}
                    barColor="#FF9800"
                    title="Critical Response Stats"
                  />
                ) : (
                  <Stack
                    alignItems="center"
                    flex={1}
                    justifyContent="center"
                    minHeight={100}
                  >
                    <VSNTypography
                      fontSize={{ md: 32, sm: 26, xs: 20 }}
                      color="#898994"
                      textAlign="center"
                    >
                      There is no data to show
                    </VSNTypography>
                  </Stack>
                )}
              </Stack>
            ) : (
              <Stack alignItems="center" flex={1} justifyContent="center">
                <CircularProgress />
              </Stack>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
};

const AssessmentPieChart = ({
  completedPercent = 0,
  notCompletedPercent = 0,
}) => {
  const chartData = [
    { value: completedPercent, name: "Completed" },
    { value: notCompletedPercent, name: "Not Completed" },
  ];

  const isMobile = window.innerWidth <= 500;

  const option = {
    color: ["#8B3F9E", "#ffffff"],
    tooltip: {
      trigger: "item",
      formatter: "{b}: {d}%",
    },
    legend: isMobile
      ? {
          orient: "horizontal",
          bottom: "0%",
          textStyle: {
            fontSize: 14,
            color: "#333",
          },
        }
      : {
          orient: "vertical",
          right: "5%",
          top: "center",
          textStyle: {
            fontSize: 14,
            color: "#333",
          },
        },
    series: [
      {
        name: "Assessment Status",
        type: "pie",
        radius: "65%",
        center: isMobile ? ["50%", "50%"] : ["30%", "50%"],
        avoidLabelOverlap: false,
        label: {
          show: true,
          position: "inside",
          formatter: "{d}%",
          fontSize: 16,
        },
        emphasis: {
          label: {
            show: true,
            fontSize: 18,
            fontWeight: "bold",
          },
        },
        labelLine: {
          show: false,
        },
        itemStyle: {
          borderColor: "#8B3F9E",
          borderWidth: 1,
        },
        animation: false,
        animationDuration: 0,
        animationDurationUpdate: 0,
        data: chartData,
      },
    ],
  };

  return <ReactECharts option={option} style={{ height: 300 }} />;
};

const CriticalResponseBarChart = ({
  data = {},
  barColor = "#3f51b5",
  title = "Critical Response Summary",
}) => {
  const labels = Object.keys(data);
  const values = Object.values(data);

  const option = {
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "shadow",
      },
    },
    grid: {
      left: "3%",
      right: "4%",
      bottom: "8%",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      data: labels,
      axisLabel: {
        interval: 0,
        formatter: (value) => {
          return value.split(" ").join("\n");
        },
      },
    },

    yAxis: {
      type: "value",
    },
    series: [
      {
        data: values,
        type: "bar",
        barWidth: "50%",
        itemStyle: {
          color: barColor,
          borderRadius: [4, 4, 0, 0],
        },
        animation: false,
        animationDuration: 0,
        animationDurationUpdate: 0,
      },
    ],
  };

  return <ReactECharts option={option} style={{ height: 400 }} />;
};

const DynamicLineChart = ({ data = {}, showLegend = true }) => {
  const xAxisData = Object?.keys(data);
  const yAxisData = Object?.values(data);

  const option = {
    color: ["#E87637"],
    tooltip: {
      trigger: "axis",
    },
    legend: false,
    grid: {
      left: "3%",
      right: "4%",
      bottom: "10%",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      data: xAxisData,
      boundaryGap: false,
      name: "Timeline",
      nameLocation: "middle",
      nameGap: 25,
      nameTextStyle: {
        fontSize: 14,
      },
    },
    yAxis: {
      type: "value",
      name: "No. Of Members",
      nameLocation: "middle",
      nameGap: 25,
      nameTextStyle: {
        fontSize: 14,
      },
    },
    series: [
      {
        name: "Value",
        type: "line",
        data: yAxisData,
        symbol: "circle",
        symbolSize: 6,
        lineStyle: {
          width: 3,
        },
      },
    ],
  };

  return <ReactECharts option={option} style={{ height: 400 }} />;
};

export default EventStatistics;
